import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, NgZone,
  afterEveryRender, computed, effect, inject, output, signal, viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { TranslationService } from '../../../core/i18n/translation.service';
import { MarkdownComponent } from '../../../components/markdown/markdown.component';
import { AuthService } from '../../../core/auth/auth.service';
import { SessionStore } from '../../../core/auth/session.store';
import { AuditAssistantApi } from '../services/audit-assistant.api';
import { AISearchResults, AIStreamEvent, AIConversationTurn, AIConversationSummary, AICreatedChecklist } from '../models/audit-assistant.model';
import { AuditAssistantResultsComponent } from './audit-assistant-results.component';

@Component({
  selector: 'app-audit-assistant',
  imports: [MarkdownComponent, AuditAssistantResultsComponent],
  templateUrl: './audit-assistant.component.html',
  styleUrl: './audit-assistant.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'onDocumentClick($event)' },
})
export class AuditAssistantComponent {
  readonly checklistEditRequested = output<string>();
  protected readonly createdChecklist = signal<AICreatedChecklist | null>(null);

  protected editChecklist(checklist: AICreatedChecklist): void {
    if (this.busy() || !/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(checklist.checklist_id)) return;
    this.close();
    this.checklistEditRequested.emit(checklist.checklist_id);
  }
  protected readonly i18n = inject(TranslationService);
  protected readonly responseMarkdown = signal('');
  protected readonly results = signal<AISearchResults | null>(null);
  protected readonly question = signal('');
  protected readonly busy = signal(false);
  protected readonly progress = signal('');
  protected readonly elapsedSeconds = signal(0);
  protected readonly elapsedTime = computed(() => {
    const seconds = this.elapsedSeconds();
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  });
  protected readonly checklistStep = computed(() =>
    ['checklist_reviewing', 'checklist_generating', 'checklist_validating', 'checklist_saving'].indexOf(this.progress()) + 1);
  private readonly zone = inject(NgZone);
  private elapsedTimer?: ReturnType<typeof setInterval>;
  protected readonly errorKey = signal('');
  readonly isOpen = signal(false);
  protected readonly closing = signal(false);
  protected readonly draft = signal('');
  protected readonly history = signal<AIConversationTurn[]>([]);
  protected readonly historyLoading = signal(false);
  protected readonly historyError = signal(false);
  protected readonly nextBefore = signal<number | null>(null);
  protected readonly menuOpen = signal(false);
  protected readonly recentConversations = signal<AIConversationSummary[]>([]);
  protected readonly menuLoading = signal(false);
  protected readonly menuError = signal(false);
  private readonly menuHost = viewChild.required<ElementRef<HTMLElement>>('conversationMenu');
  private menuSubscription?: Subscription;
  private menuOperation = 0;
  private menuFocusPending = false;
  private conversationId?: string;
  private historySubscription?: Subscription;
  private readonly dialog = viewChild.required<ElementRef<HTMLDialogElement>>('dialog');
  private closeTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly api = inject(AuditAssistantApi);
  private readonly auth = inject(AuthService);
  private readonly session = inject(SessionStore);
  private readonly conversation = viewChild.required<ElementRef<HTMLElement>>('conversation');
  private subscription?: Subscription;
  private operation = 0;
  private followAnswer = true;
  private scrollPending = false;

  private context(): string {
    const session = this.session.session();
    return session ? `${session.userCode}|${session.tenantId}|${session.schema}|${session.role}` : '';
  }

  private storageKey(context = this.context()): string {
    return `junoaudit.ai.conversation.${context}`;
  }

  private saveConversation(id?: string): void {
    this.conversationId = id;
    try {
      if (id) sessionStorage.setItem(this.storageKey(), id);
      else sessionStorage.removeItem(this.storageKey());
    } catch { /* History still works in memory when browser storage is disabled. */ }
  }

  constructor() {
    let context = this.context();
    effect(() => {
      const next = this.context();
      if (context === next) return;
      try { sessionStorage.removeItem(this.storageKey(context)); } catch { /* Optional storage. */ }
      context = next;
      this.newConversation();
    });
    afterEveryRender(() => {
      if (this.menuFocusPending && this.menuOpen()) {
        this.menuFocusPending = false;
        this.menuHost().nativeElement.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
      }
      if (!this.scrollPending) return;
      this.scrollPending = false;
      if (this.followAnswer) {
        const element = this.conversation().nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    });
    inject(DestroyRef).onDestroy(() => {
      this.closeMenu();
      this.stop();
      clearTimeout(this.closeTimer);
      const dialog = this.dialog().nativeElement;
      if (dialog.open) dialog.close();
    });
  }

  open(): void {
    clearTimeout(this.closeTimer);
    this.closing.set(false);
    this.dialog().nativeElement.showModal();
    this.isOpen.set(true);
    if (!this.conversationId && !this.question() && !this.history().length) {
      try { this.conversationId = sessionStorage.getItem(this.storageKey()) || undefined; } catch { /* Optional storage. */ }
      if (this.conversationId) this.loadHistory();
    }
  }

  protected newConversation(): void {
    this.closeMenu(true);
    this.stop();
    this.saveConversation();
    this.history.set([]);
    this.nextBefore.set(null);
    this.historyError.set(false);
    this.question.set('');
    this.responseMarkdown.set('');
    this.results.set(null);
    this.createdChecklist.set(null);
    this.draft.set('');
    this.errorKey.set('');
    this.progress.set('');
  }

  protected toggleMenu(): void {
    if (this.menuOpen()) { this.closeMenu(true); return; }
    this.menuOpen.set(true);
    this.menuFocusPending = true;
    this.loadConversations();
  }

  protected loadConversations(): void {
    this.menuSubscription?.unsubscribe();
    const operation = ++this.menuOperation;
    const context = this.context();
    this.recentConversations.set([]);
    this.menuLoading.set(true);
    this.menuError.set(false);
    this.menuSubscription = this.api.conversations(this.conversationId).subscribe({
      next: page => {
        if (operation !== this.menuOperation || context !== this.context()) return;
        this.recentConversations.set(page.results.filter(row => row.id !== this.conversationId).slice(0, 3));
        this.menuLoading.set(false);
      },
      error: () => {
        if (operation !== this.menuOperation || context !== this.context()) return;
        this.menuLoading.set(false);
        this.menuError.set(true);
      },
    });
  }

  protected selectConversation(id: string): void {
    this.newConversation();
    this.saveConversation(id);
    this.followAnswer = true;
    this.loadHistory();
  }

  protected conversationDate(value: string): string {
    return new Intl.DateTimeFormat(this.i18n.language(), { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  }

  protected closeMenu(restoreFocus = false): void {
    const wasOpen = this.menuOpen();
    this.menuOpen.set(false);
    this.menuFocusPending = false;
    ++this.menuOperation;
    this.menuSubscription?.unsubscribe();
    this.menuSubscription = undefined;
    this.menuLoading.set(false);
    this.recentConversations.set([]);
    this.menuError.set(false);
    if (wasOpen && restoreFocus) {
      this.menuHost().nativeElement.querySelector<HTMLButtonElement>('[aria-haspopup="menu"]')?.focus();
    }
  }

  protected onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen() && !this.menuHost().nativeElement.contains(event.target as Node)) this.closeMenu();
  }

  protected menuFocusOut(event: FocusEvent): void {
    if (event.relatedTarget && !this.menuHost().nativeElement.contains(event.relatedTarget as Node)) this.closeMenu();
  }

  protected menuKeydown(event: KeyboardEvent): void {
    if (!this.menuOpen()) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.toggleMenu();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeMenu(true);
    } else if (event.key === 'Tab') {
      this.closeMenu(true);
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const items = [...this.menuHost().nativeElement.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
      const current = items.indexOf(event.target as HTMLButtonElement);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 :
        (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[index]?.focus();
    }
  }

  protected loadHistory(): void {
    if (!this.conversationId || this.historyLoading() || this.busy()) return;
    const operation = ++this.operation;
    const context = this.context();
    this.historyLoading.set(true);
    this.historyError.set(false);
    this.historySubscription = this.api.history(this.conversationId, this.nextBefore() ?? undefined).subscribe({
      next: page => {
        if (operation !== this.operation || context !== this.context()) return;
        this.history.update(rows => [...page.results, ...rows]);
        this.nextBefore.set(page.next_before);
        this.historyLoading.set(false);
        this.scrollPending = this.history().length === page.results.length;
      },
      error: () => {
        if (operation !== this.operation || context !== this.context()) return;
        this.historyLoading.set(false);
        this.historyError.set(true);
      },
    });
  }

  private archiveCurrent(): void {
    if (!this.question()) return;
    const progress = this.progress();
    this.history.update(rows => [...rows, {
      id: crypto.randomUUID(), sequence: 0, user_message: this.question(),
      assistant_message: this.responseMarkdown(), presentation_json: this.results(),
      checklist_result_json: this.createdChecklist(),
      status: this.errorKey() ? 'failed' : progress === 'completed' || progress === 'refused' ? 'completed' :
        progress === 'incomplete' ? 'incomplete' : 'interrupted',
      refused: progress === 'refused', error_code: '', language: this.i18n.language(),
      created_at: new Date().toISOString(), finished_at: null,
    }]);
  }

  protected historyStatus(turn: AIConversationTurn): string {
    if (turn.refused) return 'refused';
    if (turn.status === 'streaming' || turn.status === 'interrupted') return 'stopped';
    return turn.status;
  }

  protected close(): void {
    if (this.closing()) return;
    this.closeMenu();
    if (this.busy()) this.stop();
    this.closing.set(true);
    this.closeTimer = setTimeout(() => {
      this.dialog().nativeElement.close();
      this.isOpen.set(false);
      this.closing.set(false);
    }, 220);
  }

  protected async send(): Promise<void> {
    if (this.historyLoading() || this.historyError()) return;
    if (this.busy()) { this.stop(); return; }
    const message = this.draft().trim();
    const language = this.i18n.language();
    if (!message) return;
    const operation = ++this.operation;
    const context = this.context();
    this.archiveCurrent();
    this.busy.set(true);
    this.startElapsedTimer();
    this.errorKey.set('');
    this.progress.set('preparing');
    this.question.set(message);
    this.responseMarkdown.set('');
    this.results.set(null);
    this.createdChecklist.set(null);
    this.draft.set('');
    this.followAnswer = true;
    this.scrollPending = true;
    try {
      const valid = await this.auth.ensureValidSession();
      if (operation !== this.operation || context !== this.context()) return;
      if (!valid) {
        this.fail('auditAssistant.error.session');
        return;
      }
      this.subscription = this.api.stream(message, language, this.conversationId).subscribe({
        next: event => {
          if (operation === this.operation && context === this.context()) this.receive(event);
        },
        error: error => {
          if (operation === this.operation && context === this.context()) {
            const code = error?.status === 409 ? 'ai_conversation_busy' :
              error?.status === 404 ? 'ai_conversation_not_found' : error?.error?.code;
            this.fail(code === 'ai_conversation_busy' || code === 'ai_conversation_not_found' ?
              `auditAssistant.error.${code}` : 'auditAssistant.error.connection');
          }
        },
      });
    } catch {
      if (operation === this.operation) this.fail('auditAssistant.error.connection');
    }
  }

  protected stop(): void {
    this.stopElapsedTimer();
    ++this.operation;
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.historySubscription?.unsubscribe();
    this.historySubscription = undefined;
    this.historyLoading.set(false);
    if (this.busy()) this.progress.set('stopped');
    this.busy.set(false);
  }

  protected keydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      if (!this.busy()) void this.send();
    }
  }

  protected onScroll(): void {
    const element = this.conversation().nativeElement;
    this.followAnswer = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
  }

  private receive(event: AIStreamEvent): void {
    if (event.kind === 'conversation') this.saveConversation(event.conversationId);
    if (event.kind === 'status') this.progress.set(event.status);
    if (event.kind === 'results') this.results.set(event.results);
    if (event.kind === 'checklist_created') this.createdChecklist.set(event.checklist);
    if (event.kind === 'delta') {
      this.progress.set('generating');
      this.responseMarkdown.update(text => text + event.text);
    }
    if (event.kind === 'done') {
      this.stopElapsedTimer();
      this.responseMarkdown.set(event.text);
      this.progress.set(event.status === 'incomplete' ? 'incomplete' : event.refused ? 'refused' : 'completed');
      this.busy.set(false);
    }
    if (event.kind === 'error') {
      const known = ['ai_checklist_conflict', 'ai_checklist_invalid_proposal', 'ai_checklist_write_uncertain', 'ai_checklist_size_limit',
        'ai_configuration_unavailable', 'ai_instructions_missing', 'ai_input_limit_exceeded',
        'ai_rate_limit_exceeded', 'ai_monthly_limit_exceeded', 'ai_model_unavailable',
        'ai_credentials_rejected', 'ai_provider_rate_limited', 'ai_stream_interrupted',
        'ai_provider_timeout', 'ai_provider_unavailable',
        'ai_search_limit_exceeded', 'ai_search_data_changed', 'ai_search_invalid_selection',
        'ai_bhp_access_denied', 'ai_bhp_unavailable', 'ai_bhp_invalid_response', 'ai_bhp_configuration_invalid'];
      this.fail(known.includes(event.code) ? `auditAssistant.error.${event.code}` : 'auditAssistant.error.connection');
    }
    this.scrollPending = true;
  }

  private fail(key: string): void {
    this.stopElapsedTimer();
    this.errorKey.set(key);
    this.progress.set('');
    this.busy.set(false);
    this.scrollPending = true;
  }

  private startElapsedTimer(): void {
    this.stopElapsedTimer();
    this.elapsedSeconds.set(0);
    const started = Date.now();
    this.zone.runOutsideAngular(() => {
      this.elapsedTimer = setInterval(() => {
        this.elapsedSeconds.set(Math.max(0, Math.floor((Date.now() - started) / 1000)));
      }, 1000);
    });
  }

  private stopElapsedTimer(): void {
    clearInterval(this.elapsedTimer);
    this.elapsedTimer = undefined;
  }

  protected cancel(event: Event): void {
    event.preventDefault();
    if (this.menuOpen()) { this.closeMenu(true); return; }
    this.close();
  }

  protected backdropClick(event: MouseEvent): void {
    const dialog = this.dialog().nativeElement;
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom) this.close();
  }
}
